import { Box, Button, Flex, Typography } from '@ergolabs/ui-kit';
import React, { FC, ReactNode } from 'react';

import { VestingItem } from '../../../api/vesting/vesting';
import { spfAsset } from '../../../common/assets/spfAsset';
import { AssetIcon } from '../../../common/components/AssetIcon/AssetIcon';
import {
  openConfirmationModal,
  Operation,
} from '../../../common/components/ConfirmationModal/ConfirmationModal';
import { Currency } from '../../../common/models/Currency';
import { ClaimConfirmationModal } from '../ClaimConfirmationModal/ClaimConfirmationModal';
import { ReactComponent as EmptyIcon } from './empty.svg';

export interface ClaimTableItemProps {
  readonly title: ReactNode;
  readonly vestingItems: VestingItem[];
}

export const ClaimTableItem: FC<ClaimTableItemProps> = ({
  title,
  vestingItems,
}) => {
  const remaining = vestingItems.reduce(
    (sum, vi) => sum.plus(new Currency(vi.remaining.toString(), spfAsset)),
    new Currency(0n, spfAsset),
  );

  const claimable = vestingItems.reduce(
    (sum, vi) => sum.plus(new Currency(vi.redeemable.toString(), spfAsset)),
    new Currency(0n, spfAsset),
  );

  const openClaimConfirmationModal = () => {
    openConfirmationModal(
      (next) => (
        <ClaimConfirmationModal
          title={title}
          onClose={next}
          vestingItems={vestingItems}
        />
      ),
      Operation.REDEEM,
      {
        spfAsset: claimable,
      },
    );
  };

  return (
    <Box padding={4} secondary borderRadius="l" height={250}>
      <Flex col stretch>
        <Flex.Item marginBottom={4} display="flex" align="center">
          <Typography.Title level={4}>{title}</Typography.Title>
        </Flex.Item>
        {vestingItems.length ? (
          <>
            <Flex.Item marginBottom={4}>
              <Flex align="center" justify="space-between">
                <Typography.Body strong>SPF remaining:</Typography.Body>
                <Box borderRadius="s" padding={1} bordered={false}>
                  <Flex align="center">
                    <Flex.Item marginRight={1}>
                      <AssetIcon asset={spfAsset} size="extraSmall" />
                    </Flex.Item>
                    <Typography.Body strong>
                      {remaining.toCurrencyString()}
                    </Typography.Body>
                  </Flex>
                </Box>
              </Flex>
            </Flex.Item>
            <Flex.Item marginBottom={4}>
              <Flex align="center" justify="space-between">
                <Typography.Body strong>SPF claimable:</Typography.Body>
                <Box borderRadius="s" padding={1} bordered={false}>
                  <Flex align="center">
                    <Flex.Item marginRight={1}>
                      <AssetIcon asset={spfAsset} size="extraSmall" />
                    </Flex.Item>
                    <Typography.Body strong>
                      {claimable.toCurrencyString()}
                    </Typography.Body>
                  </Flex>
                </Box>
              </Flex>
            </Flex.Item>
            <Flex.Item marginBottom={4}>
              <Flex align="center" justify="space-between">
                <Typography.Body strong>Next unlock:</Typography.Body>
                <Box borderRadius="s" padding={1} bordered={false}>
                  <Typography.Body strong>
                    {vestingItems[0].nextUnlock.toFormat('dd-MM-yyyy HH:mm:ss')}
                  </Typography.Body>
                </Box>
              </Flex>
            </Flex.Item>
            <Button
              type="primary"
              size="middle"
              onClick={openClaimConfirmationModal}
            >
              Claim SPF
            </Button>
          </>
        ) : (
          <Flex.Item flex={1}>
            <Flex stretch align="center" justify="center" col>
              <Flex.Item marginBottom={2}>
                <EmptyIcon />
              </Flex.Item>
              <Typography.Body align="center">
                You have no vesting available for this round
              </Typography.Body>
            </Flex>
          </Flex.Item>
        )}
      </Flex>
    </Box>
  );
};
